import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Thumbs, Zoom } from 'swiper/modules';


// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';

import { getImagePlaceholder } from '../../lib/utils';
import type { ProductImage } from '../../types';

interface ProductGalleryProps {
    images: ProductImage[];
    mainImage?: string | null;
    productName: string;
}

export function ProductGallery({ images, mainImage, productName }: ProductGalleryProps) {
    const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);

    // Build the full images array
    const allImages = [
        ...(mainImage ? [{ id: 'main', url: mainImage, position: -1 }] : []),
        ...images.filter(img => img.url !== mainImage),
    ].sort((a, b) => a.position - b.position);

    const displayImages = allImages.length > 0
        ? allImages
        : [{ id: 'placeholder', url: getImagePlaceholder(600, 750), position: 0 }];

    return (
        <div className="space-y-4">
            {/* Main Slider */}
            <div className="rounded-2xl overflow-hidden bg-gray-100 border border-gray-50 shadow-sm relative z-0">
                <Swiper
                    style={{
                        '--swiper-navigation-color': '#111827',
                        '--swiper-pagination-color': '#111827',
                    } as React.CSSProperties}
                    spaceBetween={10}
                    navigation={true}
                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                    zoom={true}
                    modules={[FreeMode, Navigation, Thumbs, Zoom]}
                    className="product-gallery-main aspect-[4/5] w-full"
                >
                    {displayImages.map((img) => (
                        <SwiperSlide key={img.id}>
                            <div className="swiper-zoom-container">
                                <img src={img.url} alt={productName} className="object-cover w-full h-full" />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {/* Thumbs */}
            {displayImages.length > 1 && (
                <Swiper
                    onSwiper={setThumbsSwiper}
                    spaceBetween={12}
                    slidesPerView={4}
                    freeMode={true}
                    watchSlidesProgress={true}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className="product-gallery-thumbs"
                >
                    {displayImages.map((img, index) => (
                        <SwiperSlide key={img.id} className="cursor-pointer rounded-xl overflow-hidden border-2 border-transparent transition-all box-border !w-20 !h-24 bg-gray-100">
                            <img src={img.url} alt={`Thumb ${index}`} className="w-full h-full object-cover" />
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}

            <style>{`
                .product-gallery-thumbs .swiper-slide {
                    opacity: 0.5;
                    transition: all 0.2s;
                    border-color: transparent;
                }
                .product-gallery-thumbs .swiper-slide-thumb-active {
                    opacity: 1;
                    border-color: #000;
                    transform: scale(0.95);
                }
                .swiper-button-next, .swiper-button-prev {
                    background: rgba(255,255,255,0.8);
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    backdrop-filter: blur(4px);
                }
                .swiper-button-next:after, .swiper-button-prev:after {
                    font-size: 18px;
                    font-weight: bold;
                }
            `}</style>
        </div>
    );
}
